if (!customElements.get('lg-author-articles')) {
  customElements.define('lg-author-articles', class extends HTMLElement {
    connectedCallback() {
      this.items = [];
      this.currentPage = 1;
      this.pageSize = 12;
      this.status = this.querySelector('[data-status]');
      this.list = this.querySelector('[data-articles]');
      this.pagination = this.querySelector('[data-pagination]');
      this.previous = this.querySelector('[data-previous]');
      this.next = this.querySelector('[data-next]');
      this.retry = this.querySelector('[data-retry]');
      this.onPrevious = () => this.showPage(this.currentPage - 1, true);
      this.onNext = () => this.showPage(this.currentPage + 1, true);
      this.onRetry = () => this.load();
      this.onPopState = () => this.showPage(Number(new URL(location.href).searchParams.get('author_page')) || 1);
      this.previous.addEventListener('click', this.onPrevious);
      this.next.addEventListener('click', this.onNext);
      this.retry.addEventListener('click', this.onRetry);
      window.addEventListener('popstate', this.onPopState);
      this.load();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.previous.removeEventListener('click', this.onPrevious);
      this.next.removeEventListener('click', this.onNext);
      this.retry.removeEventListener('click', this.onRetry);
      window.removeEventListener('popstate', this.onPopState);
    }

    articleURL(value) {
      if (typeof value !== 'string' || !value.startsWith('/blogs/')) return null;
      const url = new URL(value, location.origin);
      if (url.origin !== location.origin) return null;
      if (this.dataset.themeId) url.searchParams.set('preview_theme_id', this.dataset.themeId);
      else url.searchParams.delete('preview_theme_id');
      return url.pathname + url.search + url.hash;
    }

    async load() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.retry.hidden = true;
      this.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(this.dataset.source, { signal: this.controller.signal });
        if (!response.ok) throw new Error('Article source unavailable');
        const data = await response.json();
        const record = data.authors?.[this.dataset.author];
        if (data.schema_version !== 1 || !record || !Array.isArray(record.articles)) throw new Error('Invalid article data');
        this.items = record.articles.filter(item => typeof item.title === 'string' && this.articleURL(item.url));
        this.showPage(Number(new URL(location.href).searchParams.get('author_page')) || 1);
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.status.textContent = this.dataset.error;
        this.status.hidden = false;
        this.retry.hidden = false;
        this.pagination.hidden = true;
      } finally {
        this.removeAttribute('aria-busy');
      }
    }

    showPage(requestedPage, updateURL = false) {
      const pages = Math.max(1, Math.ceil(this.items.length / this.pageSize));
      this.currentPage = Math.min(pages, Math.max(1, Math.floor(requestedPage) || 1));
      const fragment = document.createDocumentFragment();
      this.items.slice((this.currentPage - 1) * this.pageSize, this.currentPage * this.pageSize).forEach(item => {
        const li = document.createElement('li');
        li.className = 'lg-author__article';
        const link = document.createElement('a');
        link.href = this.articleURL(item.url);
        if (item.image?.url) {
          let imageURL;
          try { imageURL = new URL(item.image.url); } catch (_) { imageURL = null; }
          if (imageURL?.protocol === 'https:' && (imageURL.hostname === 'cdn.shopify.com' || imageURL.hostname === 'leoguarbikes.com')) {
            imageURL.searchParams.set('width', '720');
            const image = document.createElement('img');
            image.src = imageURL.href;
            image.alt = typeof item.image.alt === 'string' ? item.image.alt : item.title;
            image.width = 720;
            image.height = 450;
            image.loading = 'lazy';
            link.append(image);
          }
        }
        const content = document.createElement('div');
        const heading = document.createElement('h3');
        heading.textContent = item.title;
        content.append(heading);
        if (typeof item.published_at === 'string' && /^\d{4}-\d{2}-\d{2}/.test(item.published_at)) {
          const date = new Date(item.published_at.slice(0, 10) + 'T00:00:00Z');
          if (!Number.isNaN(date.valueOf())) {
            const time = document.createElement('time');
            time.dateTime = item.published_at.slice(0, 10);
            time.textContent = new Intl.DateTimeFormat(document.documentElement.lang || 'en', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' }).format(date);
            content.append(time);
          }
        }
        link.append(content);
        li.append(link);
        fragment.append(li);
      });
      this.list.replaceChildren(fragment);
      this.status.hidden = this.items.length > 0;
      this.status.textContent = this.items.length ? '' : this.dataset.empty;
      this.pagination.hidden = pages <= 1;
      this.previous.disabled = this.currentPage === 1;
      this.next.disabled = this.currentPage === pages;
      this.querySelector('[data-page-status]').textContent = this.dataset.pageLabel.replace('{current}', this.currentPage).replace('{total}', pages);
      if (updateURL) {
        const url = new URL(location.href);
        url.searchParams.set('author_page', this.currentPage);
        history.pushState(null, '', url);
        this.scrollIntoView({ block: 'start', behavior: 'auto' });
      }
    }
  });
}
